import { create } from 'zustand';
import { combine } from 'zustand/middleware';
import { ApiService, Store } from 'src/data/services/Services';
import errorHandler from 'src/core/utils/errorHandler';
import Translations from 'src/core/utils/Translations';
import _ from 'lodash';

interface TimetableEntry {
	pageid: string | number;
	eventUuid: string;
}

interface MyTimeTableState {
	myTimeTableData: TimetableEntry[];
	isLoading: boolean;
	// True when fetching the favorites from the server failed: the local list may be out of sync, so liking is
	// blocked until a successful refresh (server is the source of truth).
	loadFailed: boolean;
}

const initialState: MyTimeTableState = {
	myTimeTableData: [],
	isLoading: true,
	loadFailed: false,
};

// Per-pageID toggle sequence: lets a newer toggle invalidate the reconcile of an older, still-in-flight one
// so a stale response cannot clobber newer input.
const toggleSeq = new Map<string | number, number>();

export const useMyTimeTableStore = create(
	combine(initialState, (set, get) => ({
		init: async () => {
			try {
				const value = await Store.getObject('myTimetable');
				if (value) {
					set({ myTimeTableData: value as TimetableEntry[], isLoading: false });
				} else {
					set({ myTimeTableData: [], isLoading: false });
				}
			} catch (error) {
				console.error('Error loading MyTimeTable from storage:', error);
				set({ isLoading: false });
			}
		},

		// Optimistic local update for a snappy UI, then sync with the server (source of truth) and reconcile if
		// they differ.
		toggleFavorite: async (pageID: string | number, eventUuid: string) => {
			// Liking is blocked while the server state is unknown (a failed load): prompt a refresh instead of writing
			// a like the server never sees.
			if (get().loadFailed) {
				errorHandler(Translations.errorFavoritesUnavailableTitle, Translations.errorFavoritesRefresh);
				return;
			}

			const previous = [...get().myTimeTableData];
			const exists = _.find(previous, ['pageid', pageID]);

			const updated: TimetableEntry[] = exists
				? previous.filter((page) => page.pageid !== pageID)
				: [...previous, { pageid: pageID, eventUuid }];

			Store.save('myTimetable', updated);
			set({ myTimeTableData: updated });

			// Sequence guard against rapid re-toggling of the same item.
			const token = (toggleSeq.get(pageID) ?? 0) + 1;
			toggleSeq.set(pageID, token);

			try {
				const { data: result } = await ApiService.endpoints.favorites.toggle({ eventUuid, pageId: pageID });
				if (toggleSeq.get(pageID) !== token || typeof result?.is_favorite !== 'boolean') {
					return;
				}

				const present = !!_.find(get().myTimeTableData, ['pageid', pageID]);
				if (result.is_favorite !== present) {
					const reconciled: TimetableEntry[] = result.is_favorite
						? [...get().myTimeTableData, { pageid: pageID, eventUuid }]
						: get().myTimeTableData.filter((page) => page.pageid !== pageID);

					Store.save('myTimetable', reconciled);
					set({ myTimeTableData: reconciled });
				}
			} catch {
				// Server rejected the toggle: roll back so local state never diverges from the server, and tell the user
				// the like was not saved.
				if (toggleSeq.get(pageID) === token) {
					Store.save('myTimetable', previous);
					set({ myTimeTableData: previous });
					errorHandler(Translations.errorDefaultSubject, Translations.errorFavoriteToggleFailed);
				}
			}
		},

		// Fetch the favorites for an event from the server and make them leading for that event.
		syncFromServer: async (eventUuid: string) => {
			if (!eventUuid) {
				return;
			}

			try {
				const { data } = await ApiService.endpoints.favorites.list({ eventUuid });
				if (!data || !Array.isArray(data.page_ids)) {
					return;
				}

				const others = get().myTimeTableData.filter((page) => page.eventUuid !== eventUuid);
				const fromServer: TimetableEntry[] = data.page_ids.map((pageid) => ({ pageid, eventUuid }));
				const merged = [...others, ...fromServer];

				Store.save('myTimetable', merged);
				set({ myTimeTableData: merged, loadFailed: false });
			} catch {
				// Could not load the server favorites: flag it (once) so liking is blocked until a successful refresh,
				// and tell the user.
				if (!get().loadFailed) {
					set({ loadFailed: true });
					errorHandler(Translations.errorDefaultSubject, Translations.errorFavoritesLoadFailed);
				}
			}
		},

		checkFavorite: (pageID: string | number) => {
			return !!_.find(get().myTimeTableData, ['pageid', pageID]);
		},
	})),
);
